import { useEffect } from 'react';
import useAuth from './../../auth';
import {
    useRingSettings,
    useEvent,
    useTickets,
    useCurrentUser,
    useLoadCustomizationScripts,
} from '@bizzabo/entities';
import { useFeatureToggle } from '@bizzabo/feature-toggle-client';

const appendCss = css => {
    const style = document.createElement('style');
    style.id = 'internal-css';
    style.innerHTML = css;
    // Append link element to HTML head
    document.head.appendChild(style);
};

const appendJs = (scriptContent, element = 'body') => {
    if (!scriptContent) {
        return;
    }
    const fragment = document.createRange().createContextualFragment(scriptContent);
    document[element].appendChild(fragment);
};

export const areAllAllowedFilesLoaded = (customization, s3Files) => {
    if (!customization) {
        return false;
    }
    const { enableJsInject, enableCssInject, customJsHeadFile, customJsBodyFile, customCssFile } =
        customization;

    //should load every specific file (jsHead, jsBody, jsCss)
    const shouldLoadHead = enableJsInject && customJsHeadFile;
    const shouldLoadBody = enableJsInject && customJsBodyFile;
    const shouldLoadCss = enableCssInject && customCssFile;
    //did load the file (will return true if file does not need to be loaded)
    const isHeadLoaded = shouldLoadHead ? !!s3Files?.jsHeadContent?.data : true;
    const isBodyLoaded = shouldLoadBody ? !!s3Files?.jsBodyContent?.data : true;
    const isCssLoaded = shouldLoadCss ? !!s3Files?.cssContent?.data : true;
    return isHeadLoaded && isBodyLoaded && isCssLoaded;
};

const useInternalJS = (eventUniqueName: string, embedMode: boolean) => {
    const isAuth = useAuth(eventUniqueName, embedMode);
    const { data: event } = useEvent(eventUniqueName);
    const user = useCurrentUser(event?.id, { enabled: isAuth });
    const tickets = useTickets(event?.id, { enabled: isAuth });
    const s3Files = useLoadCustomizationScripts(event?.id, event?.accountId);
    const { data: ringSettings } = useRingSettings(event?.id, event?.accountId, {
        enabled: !!event,
        retry: 1,
    });
    const isSupportedEvent = useFeatureToggle('venue-internal-scripts');
    const objectsIsLoading = event || user.isLoading || tickets.isLoading;
    const objectDataExists = event?.uniqueName && user?.data && tickets?.data;
    const allFilesLoaded = areAllAllowedFilesLoaded(ringSettings?.customization, s3Files);

    useEffect(() => {
        if (
            !isSupportedEvent ||
            !objectsIsLoading ||
            !ringSettings ||
            !objectDataExists ||
            !allFilesLoaded
        ) {
            return;
        }
        const { customization } = ringSettings;
        if (!customization) {
            return;
        }

        const { enableJsInject, enableCssInject } = customization;

        const BODY = 'body';
        const HEAD = 'head';

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        window.bz_public_obj = {
            event,
            tickets: tickets && tickets.data,
            user: tickets && user.data,
        };
        enableJsInject && s3Files.jsHeadContent && appendJs(s3Files.jsHeadContent?.data, HEAD);
        enableJsInject && s3Files.jsBodyContent && appendJs(s3Files.jsBodyContent?.data, BODY);
        enableCssInject && s3Files.cssContent && appendCss(s3Files.cssContent?.data);

        return () => {};
    }, [
        event?.uniqueName,
        user?.data,
        tickets?.data,
        ringSettings,
        s3Files?.jsBodyContent?.data,
        s3Files?.jsHeadContent?.data,
        s3Files?.cssContent?.data,
    ]);
};

export default useInternalJS;
